import type {ConfigData, PreloadParams} from '@diplodoc/client/ssr';
import type {Build, EntryData, OpenapiCompanionEntry, PageData, Run} from '~/commands/build';
import type {Toc, TocItem} from '~/core/toc';
import type {LeadingPage} from '~/core/leading';

import {basename, dirname, extname, join} from 'node:path';
import {v4 as uuid} from 'uuid';
import pmap from 'p-map';
import {preprocess} from '@diplodoc/client/ssr';
import {isFileExists} from '@diplodoc/transform/lib/utilsFS';

import {Template} from '~/core/template';
import {
    fallbackLang,
    isExternalHref,
    isMediaLink,
    normalizePath,
    own,
    setExt,
    zip,
} from '~/core/utils';
import {getHooks as getBuildHooks} from '~/commands/build';
import {getHooks as getTocHooks} from '~/core/toc';
import {getHooks as getLeadingHooks} from '~/core/leading';
import {getHooks as getMarkdownHooks} from '~/core/markdown';
import {getHooks as getMetaHooks} from '~/core/meta';
import {ASSETS_FOLDER} from '~/constants';

import {getHooks as getRedirectsHooks} from '../../services/redirects';
import {getHooks as getEntryHooks} from '../../services/entry';
import {getHooks as getSearchHooks} from '../../services/search';
import {buildLlmsAlternate} from '../output-md/utils';

import {filterBundledExtensionAssets, getBaseMdItPlugins, getCustomMdItPlugins} from './utils';

const tocJS = (path: NormalizedPath) => setExt(path, '.js');

const __Entry__ = Symbol('isEntry');

export class OutputHtml {
    apply(program: Build) {
        getBuildHooks(program)
            .BeforeRun.for('html')
            .tap('Html', async (run) => {
                const setBaseHref = (template: Template) => {
                    template.setBaseHref(run.config.baseHref);
                };

                getEntryHooks(run.entry).Page.tap('Html.BaseHref', setBaseHref);
                getRedirectsHooks(run.redirects).Page.tap('Html.BaseHref', setBaseHref);
                getSearchHooks(run.search).Page.tap('Html.BaseHref', setBaseHref);

                getTocHooks(run.toc).Dump.tapPromise('Html', async (vfile) => {
                    await run.toc.walkItems([vfile.data as Toc], (item: Toc | TocItem) => {
                        if (own(item, 'hidden') && item.hidden) {
                            return undefined;
                        }

                        item.id = uuid();

                        if (own<string, 'href'>(item, 'href') && !isExternalHref(item.href)) {
                            const fileExtension: string = extname(item.href);
                            const filename: string = basename(item.href, fileExtension) + '.html';

                            item.href = normalizePath(
                                join(dirname(vfile.path), dirname(item.href), filename),
                            );
                        }

                        return item;
                    });
                });

                getMetaHooks(run.meta).Dump.tap('Html', (meta, file) => {
                    // Add llms.txt describedby link when llms is enabled or llms.url is set.
                    // The relative href is computed from the article's location to the toc dir.
                    // Include files (_includes/) are not part of any toc, so skip them.
                    if (!file.includes('/_includes/') && !file.startsWith('_includes/')) {
                        try {
                            const tocDir = dirname(run.toc.for(file).path) as NormalizedPath;
                            const llmsAlternate = buildLlmsAlternate(
                                run.config.llms,
                                file,
                                tocDir,
                                run.config.baseHref,
                            );
                            if (llmsAlternate) {
                                meta.alternate = [...(meta.alternate || []), llmsAlternate];
                            }
                        } catch {
                            // File is not part of any toc (e.g. standalone include) — skip llms link.
                        }
                    }

                    return filterBundledExtensionAssets(meta);
                });

                // Dump Toc to js file
                getTocHooks(run.toc).Dump.tapPromise(
                    {name: 'Html', stage: Infinity},
                    async (vfile) => {
                        vfile.format(
                            (data) => `window.__DATA__.data.toc = ${JSON.stringify(data)};`,
                        );
                        vfile.path = setExt(vfile.path, 'js');
                    },
                );

                // Add link to dumped toc.js to html template
                getEntryHooks(run.entry).Page.tap('Html', (template) => {
                    if (!template.is(__Entry__)) {
                        return;
                    }

                    const toc = run.toc.for(template.path);

                    template.addScript(tocJS(toc.path), {position: 'state'});
                });

                // Transform any entry to final html page
                getEntryHooks(run.entry).Dump.tapPromise('Html', async (vfile) => {
                    const toc = await run.toc.dump(vfile.path);

                    const data = getStateData(vfile.data);
                    const state = await run.entry.state(vfile.path, data);
                    const template = new Template(vfile.path, state.lang, [__Entry__]);
                    template.setCspDisabled(Boolean(run.config.disableCsp));

                    const html = injectOpenapiCompanionComment(
                        run,
                        vfile.path,
                        await run.entry.page(template, state, toc.data),
                    );
                    vfile.path = setExt(vfile.path, '.html');
                    vfile.format(() => html);
                    Object.assign(vfile.info, data);
                });

                getEntryHooks(run.entry).Dump.tap({name: 'Html', stage: Infinity}, (entry) => {
                    const maxContentLength = run.config.content.maxHtmlSize;
                    const contentLength = entry.data.content.data.toString().length;

                    if (contentLength < maxContentLength) {
                        return;
                    }

                    run.logger.error(
                        'YFM012',
                        `${entry.data.path}: YFM012 / Filesize limit exceeded: ${contentLength} (limit is ${maxContentLength})`,
                    );
                });

                // Transform Page Constructor yfm blocks
                getLeadingHooks(run.leading).Plugins.tap('Html', (plugins) => {
                    return plugins.concat(async function (leading) {
                        if (!leading.blocks) {
                            return leading;
                        }

                        const {path, lang, vars} = this;
                        const options = {lang: fallbackLang(lang)} as PreloadParams;

                        const strings = new Set<string>();
                        const extract = (_lang: string, string: string) => {
                            strings.add(string);
                            return string;
                        };

                        preprocess(leading as ConfigData, options, extract);

                        const keys = [...strings];
                        if (!keys.length) {
                            return leading;
                        }

                        const values = zip(
                            keys,
                            await pmap(keys, async (string) => {
                                const {content, deps, assets} = await run.markdown.inspect(
                                    path,
                                    string,
                                    vars,
                                );

                                return run.transform(path, content, {
                                    deps: deps,
                                    assets,
                                });
                            }),
                        );
                        const compose = (_lang: string, string: string) =>
                            values[string][0] as string;

                        return preprocess(leading as ConfigData, options, compose) as LeadingPage;
                    });
                });

                getMarkdownHooks(run.markdown).Plugins.tap('Html', (plugins) => {
                    return plugins.concat(getBaseMdItPlugins()).concat(getCustomMdItPlugins());
                });

                getLeadingHooks(run.leading).Dump.tapPromise('Html', async (vfile) => {
                    vfile.data = await run.leading.walkLinks(
                        vfile.data,
                        getHref(run.input, vfile.path),
                    );
                });

                // Transform markdown to html
                getMarkdownHooks(run.markdown).Dump.tapPromise('Html', async (vfile) => {
                    const deps = await run.markdown.deps(vfile.path);
                    const assets = await run.markdown.assets(vfile.path);

                    for (const {path, size} of assets) {
                        if (!isMediaLink(path)) {
                            continue;
                        }

                        if (typeof size === 'number' && size > run.config.content.maxAssetSize) {
                            run.logger.error(
                                'YFM013',
                                `${path}: YFM013 / File asset limit exceeded: ${size} (limit is ${run.config.content.maxAssetSize})`,
                            );
                        }
                    }

                    const [result, env] = await run.transform(vfile.path, vfile.data, {
                        deps,
                        assets,
                    });

                    run.meta.addResources(vfile.path, env.meta);

                    vfile.data = result;
                    vfile.info = {...vfile.info, ...env};
                });

                getRedirectsHooks(run.redirects).Release.tap('Html', () => {
                    // Do not save redirects.yaml for html mode
                    return null;
                });
            });

        // Copy project assets to output
        getBuildHooks(program)
            .AfterRun.for('html')
            .tapPromise('Html', async (run) => {
                // TODO: we copy all files. Required to copy only used files.
                // Look at the same copy process in output-md feature.
                await run.copy(run.input, run.output, ['**/*.yaml', '**/*.md']);
                // `highlight-styles/**` are build-time inputs for theme.css generation only;
                // they must not be shipped into every doc's `_bundle` (mirrors `search-extension/**`).
                await run.copy(ASSETS_FOLDER, run.bundlePath, [
                    'search-extension/**',
                    'highlight-styles/**',
                ]);

                const assetsPath = join(run.input, '_assets');
                if (run.exists(assetsPath)) {
                    await run.copy(assetsPath, join(run.output, '_assets'));
                }
            });

        // Generate redirects
        getBuildHooks(program)
            .AfterRun.for('html')
            .tapPromise('Html', async (run) => {
                const langRelativePath: RelativePath = `./${run.config.lang}/index.html`;
                const langPath = join(run.output, langRelativePath);
                const pagePath = join(run.output, 'index.html');

                // Generate root lang redirect if it doesn't exists
                // TODO: regenerate redirect if link changed
                if (!run.exists(pagePath) && run.exists(langPath)) {
                    const content = await run.redirects.page('./', langRelativePath);
                    await run.write(pagePath, content, true);
                }

                // Generate redirect for each record in redirects.files section
                await pmap(
                    run.redirects.files,
                    async ({from, to}) => {
                        const content = await run.redirects.page(from, to);
                        await run.write(join(run.output, from), content, true);
                    },
                    {concurrency: 30},
                );
            });
    }
}

/**
 * Bakes the OpenAPI spec companion hint into the static leading page HTML.
 *
 * For md2md docs the viewer injects this comment at serve time, but md2html is served
 * statically (no viewer), so the page itself must carry it. The companion is registered by
 * the openapi includer wrapper on `run.openapiCompanions`, and only when a companion file is
 * actually emitted (i.e. not for `renderMode: hidden`) — so the comment is render-mode
 * dependent. HTML comments authored in Markdown are stripped during transform, hence the
 * comment is injected straight into the rendered `<body>`.
 */
function injectOpenapiCompanionComment(run: Run, entryPath: NormalizedPath, html: string): string {
    const companions = (run as Run & {openapiCompanions?: OpenapiCompanionEntry[]})
        .openapiCompanions;

    if (!Array.isArray(companions) || companions.length === 0) {
        return html;
    }

    const pageNoExt = setExt(entryPath, '');
    const entry = companions.find((companion) => companion.leadingPage === pageNoExt);

    if (!entry) {
        return html;
    }

    // The companion sits next to the leading page, so a relative file name is a valid link.
    const target = basename(entry.companionPath);
    const comment = `<!-- json-схема со спецификацией доступна по ссылке: ${target} -->`;

    return html.includes('</body>')
        ? html.replace('</body>', `        ${comment}\n    </body>`)
        : `${html}\n${comment}`;
}

function getStateData(entry: EntryData): PageData {
    if (entry.type === 'yaml') {
        return {
            leading: true,
            data: entry.content.data,
            meta: entry.meta,
            title: entry.content.data.title || entry.meta.title || '',
        };
    } else {
        return {
            leading: false,
            html: entry.content.toString(),
            meta: entry.meta,
            headings: entry.info.headings,
            title: entry.info.title || entry.meta.title || '',
        };
    }
}

function getHref(root: AbsolutePath, path: NormalizedPath) {
    return function (href: string) {
        if (isExternalHref(href)) {
            return href;
        }

        if (!href.startsWith('/')) {
            href = normalizePath(join(dirname(path), href));
        }

        const filePath = join(root, href);

        if (isFileExists(filePath)) {
            href = href.replace(/\.(md|ya?ml)$/gi, '.html');
        } else if (!/.+\.\w+$/gi.test(href)) {
            // TODO: isFileExists index.md or index.yaml
            href = href + (href.endsWith('/') ? '' : '/') + 'index.html';
        }

        return href;
    };
}
